import {
  Application,
  CanvasRenderer,
  Text,
  UIBaseElement,
  WebGLRenderer,
  type Camera2d
} from "melonjs";
import { computeP0Layout } from "@h5-rpg/app-shell";
import type {
  ContentBundle,
  GameControllerPort,
  LifecycleProbePort,
  Rect
} from "@h5-rpg/contracts";

type DrawRenderer = CanvasRenderer | WebGLRenderer;

export interface RpgSkillAnimationCue {
  sequence: number;
  animationId: number;
  targetIndex: number;
  amount: number;
  durationMs: number;
  kind: "damage" | "healing";
}

export interface RpgSkillAnimationFrame {
  active: boolean;
  animationId: number;
  targetIndex: number;
  amount: number;
  phase: "windup" | "impact" | "release" | "done";
  progress: number;
  atlasFrame: number;
  effectScale: number;
  alpha: number;
  numberOffsetY: number;
  kind: "damage" | "healing";
}

/**
 * Reusable skill-animation clock. It deliberately owns only presentation
 * timing; game rules emit immutable cues and remain testable without Canvas.
 */
export class RpgSkillAnimationTimeline {
  private cue: RpgSkillAnimationCue | null = null;
  private elapsedMs = 0;

  sync(cue: RpgSkillAnimationCue | null): void {
    if (!cue) return;
    if (this.cue?.sequence === cue.sequence) return;
    this.cue = { ...cue, durationMs: Math.max(1, cue.durationMs) };
    this.elapsedMs = 0;
  }

  update(deltaMs: number): boolean {
    if (!this.cue || this.elapsedMs >= this.cue.durationMs) return false;
    this.elapsedMs = Math.min(
      this.cue.durationMs,
      this.elapsedMs + Math.max(0, deltaMs)
    );
    return true;
  }

  clear(): void {
    this.cue = null;
    this.elapsedMs = 0;
  }

  frame(): RpgSkillAnimationFrame | null {
    if (!this.cue) return null;
    const progress = Math.min(1, this.elapsedMs / this.cue.durationMs);
    const phase =
      progress < 0.24
        ? "windup"
        : progress < 0.62
          ? "impact"
          : progress < 1
            ? "release"
            : "done";
    const impactProgress = Math.min(
      1,
      Math.max(0, (progress - 0.2) / 0.42)
    );
    return {
      active: phase !== "done",
      animationId: this.cue.animationId,
      targetIndex: this.cue.targetIndex,
      amount: this.cue.amount,
      phase,
      progress,
      atlasFrame: Math.min(3, Math.floor(progress * 4)),
      effectScale:
        phase === "windup"
          ? 0.55 + progress * 1.8
          : 1 + Math.sin(impactProgress * Math.PI) * 0.28,
      alpha: progress < 0.72 ? 1 : Math.max(0, (1 - progress) / 0.28),
      numberOffsetY: -Math.max(0, progress - 0.28) * 34,
      kind: this.cue.kind
    };
  }
}

export type P0WindowLayerOptions = {
  controller: GameControllerPort;
  content: ContentBundle;
  width: number;
  height: number;
  probe?: LifecycleProbePort;
};

const drawPanel = (
  renderer: DrawRenderer,
  rect: Rect,
  fill: string,
  stroke: string
): void => {
  renderer.setColor(fill);
  renderer.fillRoundRect(rect.x, rect.y, rect.width, rect.height, 18);
  renderer.setColor(stroke);
  renderer.strokeRoundRect(rect.x, rect.y, rect.width, rect.height, 18);
};

class WindowLayer extends UIBaseElement {
  private readonly releaseProbe: (() => void) | undefined;
  private readonly title: Text;
  private readonly description: Text;
  private readonly closeLabel: Text;
  private visible = false;

  constructor(private readonly options: P0WindowLayerOptions) {
    super(0, 0, options.width, options.height);
    this.anchorPoint.set(0, 0);
    this.floating = true;
    this.alwaysUpdate = true;
    this.isClickable = false;
    this.releaseProbe = options.probe?.register("windowLayer");

    const map = options.content.maps[options.controller.getSnapshot().state.mapId];
    if (!map) {
      throw new Error("WindowLayer requires the current map");
    }
    const layout = computeP0Layout(
      options.width,
      options.height,
      map.width,
      map.height,
      options.controller.getSnapshot().state.position
    );
    this.title = this.createText(
      layout.modal.x + 28,
      layout.modal.y + 30,
      "暂停菜单",
      28,
      "#f8f3ea",
      true
    );
    this.description = this.createText(
      layout.modal.x + 28,
      layout.modal.y + 82,
      "窗口位于游戏循环内\n打开时，地图与战斗输入被阻断",
      17,
      "#aebfd2"
    );
    this.closeLabel = this.createText(
      layout.modalCloseButton.x + layout.modalCloseButton.width / 2,
      layout.modalCloseButton.y + layout.modalCloseButton.height / 2,
      "继续游戏",
      20,
      "#fff7ea",
      true,
      "center"
    );
    this.addChild(this.title, 2);
    this.addChild(this.description, 2);
    this.addChild(this.closeLabel, 2);
    this.syncVisibility();
  }

  override update(dt: number): boolean {
    const changed = this.syncVisibility();
    return super.update(dt) || changed;
  }

  override draw(renderer: DrawRenderer, viewport?: Camera2d): void {
    if (this.options.controller.getSnapshot().topWindow !== "menu") {
      return;
    }
    const state = this.options.controller.getSnapshot().state;
    const map = this.options.content.maps[state.mapId];
    if (!map) {
      return;
    }
    const layout = computeP0Layout(
      this.options.width,
      this.options.height,
      map.width,
      map.height,
      state.position
    );
    renderer.setColor("#02050acc");
    renderer.fillRect(0, 0, this.options.width, this.options.height);
    drawPanel(renderer, layout.modal, "#152033f2", "#78a4cb");
    drawPanel(renderer, layout.modalCloseButton, "#cf6145", "#ffc47f");
    super.draw(renderer, viewport);
  }

  override destroy(): void {
    this.releaseProbe?.();
    super.destroy();
  }

  private createText(
    x: number,
    y: number,
    text: string,
    size: number,
    color: string,
    bold = false,
    align: "left" | "center" = "left"
  ): Text {
    const label = new Text(x, y, {
      font: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      size,
      fillStyle: color,
      text,
      textAlign: align,
      anchorPoint: align === "center" ? "center" : "top-left"
    });
    label.floating = true;
    if (bold) {
      label.bold();
    }
    return label;
  }

  private syncVisibility(): boolean {
    const nextVisible =
      this.options.controller.getSnapshot().topWindow === "menu";
    const changed = this.visible !== nextVisible;
    this.visible = nextVisible;
    this.setOpacity(nextVisible ? 1 : 0);
    return changed;
  }
}

export const mountP0WindowLayer = (
  applicationValue: unknown,
  options: P0WindowLayerOptions
): (() => void) => {
  const application = applicationValue as Application;
  const layer = new WindowLayer(options);
  layer.name = "p0-window-layer";
  application.world.addChild(layer, Number.MAX_SAFE_INTEGER);
  let disposed = false;
  return () => {
    if (disposed) {
      return;
    }
    disposed = true;
    if (layer.ancestor) {
      application.world.removeChildNow(layer);
    }
  };
};
